import { z } from "zod";

//
// Primitives
//

const PrimitiveValue = z.number().or(z.boolean()).or(z.string());
const ArrayValue = z.array(z.string());
const ObjectValue = z.record(z.string());

export const ExporterPropertyValue = PrimitiveValue.or(ArrayValue).or(ObjectValue);
export const ExporterPropertyType = z.enum(["Enum", "Boolean", "String", "Number", "Array", "Object", "Code"]);

export type ExporterPropertyDefinitionValue = z.infer<typeof ExporterPropertyValue>;
export type ExporterPropertyType = z.infer<typeof ExporterPropertyType>;

//
// Property
//

const PropertyDefinitionBase = z.object({
  key: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.string().optional(),
  dependsOn: z.record(z.boolean()).optional(),
});

export const ExporterPropertyDefinitionEnumOption = z.object({
  label: z.string(),
  description: z.string(),
});

export const ExporterPropertyDefinitionEnum = PropertyDefinitionBase.extend({
  type: z.literal(ExporterPropertyType.Enum.Enum),
  options: z.record(ExporterPropertyDefinitionEnumOption),
  default: z.string(),
});

export const ExporterPropertyDefinitionBoolean = PropertyDefinitionBase.extend({
  type: z.literal(ExporterPropertyType.Enum.Boolean),
  default: z.boolean(),
});

export const ExporterPropertyDefinitionString = PropertyDefinitionBase.extend({
  type: z.literal(ExporterPropertyType.Enum.String),
  default: z.string(),
  isMultiline: z.boolean().optional(),
});

export const ExporterPropertyDefinitionNumber = PropertyDefinitionBase.extend({
  type: z.literal(ExporterPropertyType.Enum.Number),
  default: z.number(),
});

export const ExporterPropertyDefinitionArray = PropertyDefinitionBase.extend({
  type: z.literal(ExporterPropertyType.Enum.Array),
  default: ArrayValue,
});

export const ExporterPropertyDefinitionObject = PropertyDefinitionBase.extend({
  type: z.literal(ExporterPropertyType.Enum.Object),
  default: ObjectValue,
  allowedKeys: z
    .object({
      options: z.string().array(),
      type: z.string(),
    })
    .optional(),
  allowedValues: z
    .object({
      type: z.string(),
    })
    .optional(),
});

export const ExporterPropertyDefinitionCode = PropertyDefinitionBase.extend({
  type: z.literal(ExporterPropertyType.Enum.Code),
  default: z.string(),
  language: z.string(),
});

export const ExporterPropertyDefinition = z.discriminatedUnion("type", [
  ExporterPropertyDefinitionEnum,
  ExporterPropertyDefinitionBoolean,
  ExporterPropertyDefinitionString,
  ExporterPropertyDefinitionNumber,
  ExporterPropertyDefinitionArray,
  ExporterPropertyDefinitionObject,
  ExporterPropertyDefinitionCode,
]);

export type ExporterPropertyDefinitionEnum = z.infer<typeof ExporterPropertyDefinitionEnum>;
export type ExporterPropertyDefinitionBoolean = z.infer<typeof ExporterPropertyDefinitionBoolean>;
export type ExporterPropertyDefinitionString = z.infer<typeof ExporterPropertyDefinitionString>;
export type ExporterPropertyDefinitionNumber = z.infer<typeof ExporterPropertyDefinitionNumber>;
export type ExporterPropertyDefinitionArray = z.infer<typeof ExporterPropertyDefinitionArray>;
export type ExporterPropertyDefinitionObject = z.infer<typeof ExporterPropertyDefinitionObject>;
export type ExporterPropertyDefinition = z.infer<typeof ExporterPropertyDefinition>;
export type ExporterPropertyDefinitionEnumOption = z.infer<typeof ExporterPropertyDefinitionEnumOption>;

//
// Value
//

export const ExporterPropertyValueMap = z.record(ExporterPropertyValue);
export type ExporterPropertyValueMap = z.infer<typeof ExporterPropertyValueMap>;
